import { Text, Stack, tokens, Icon, Alert } from '@hotjar/hotsauce';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import type { CountryCode } from '@hotjar/country-lib';
import { countryRequiresState } from '@hotjar/country-lib';
import type { PartialDeep } from 'type-fest';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { StateDropdown } from './state-dropdown/StateDropdown';
import { CountryDropdown } from './country-dropdown/CountryDropdown';
import type { FORM_NAME } from '~/subscription-management/utils/tracking';
import { trackFormSubmit } from '~/subscription-management/utils/tracking';
import { FormField } from './FormField';

export interface FormInputs {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  country: CountryCode;
  state: string | null;
  county: string | null;
  zipCode: string | null;
  companyName: string;
  taxNumber: string | null;
}

export const FORM_ID = 'invoice-details';

const S = {
  Text: styled(Text)`
    margin: 0;
  `,
  NoTaxNumberWarningContainer: styled.div`
    padding: ${tokens.spacer.x2} ${tokens.spacer.x2} ${tokens.spacer.x2} ${tokens.spacer.x3};
    background-color: ${tokens.color.ui_background};
  `,
  InfoIcon: styled(Icon)`
    margin-right: ${tokens.spacer.x2};
  `,
};

interface Props {
  formId: string;
  accountId: number;
  defaultValues: PartialDeep<FormInputs, { recurseIntoArrays: true }>;
  onFormChange?(data: FormInputs): void;
  onSubmit(data: FormInputs): void;
  formName: FORM_NAME;
  error?: string;
}

export function InvoiceDetailsForm({
  onFormChange,
  defaultValues,
  formId,
  onSubmit,
  accountId,
  formName,
  error,
}: Props): JSX.Element {
  const { t: tInvoiceDetails } = useTranslationWithPrefix('checkout:wizard.invoice-details');

  const {
    register,
    formState: { errors },
    watch,
    getValues,
    handleSubmit,
  } = useForm<FormInputs>({ defaultValues });

  const watchCountry = watch('country');

  // For those countries tax number is required.
  // Because of this, for organizations in those countries we need to get the state as well
  const shouldShowState = countryRequiresState(watchCountry);

  const sharedProps = { t: tInvoiceDetails, register };

  const getTaxMessage = (country: CountryCode) => {
    switch (country) {
      case 'CL':
        return 'chile-tax-number-compliance-message';
      case 'RU':
        return 'russia-tax-number-compliance-message';
      default:
        return undefined;
    }
  };

  const taxMessageString = getTaxMessage(watchCountry);

  const handleValidSubmit = () => onSubmit(getValues());

  const handleInvalidSubmit = () => {
    const errorMessage = Object.entries(errors)
      .map(([key, value]) => `${key} has ${value.type} error`)
      .join(', ');

    trackFormSubmit({
      name: formName,
      accountId,
      submitSuccess: false,
      errorCode: 'InvalidFormat',
      errorMessage,
    });
  };

  return (
    <form
      id={formId}
      onChange={() => (onFormChange ? onFormChange(getValues()) : null)}
      onSubmit={handleSubmit(handleValidSubmit, handleInvalidSubmit)}
    >
      <Stack preset="form">
        {error !== undefined && <Alert kind="danger">{error}</Alert>}

        <S.Text as="p" textType="subtitle1">
          {tInvoiceDetails('subtitle.billing-address')}
        </S.Text>

        <FormField
          {...sharedProps}
          error={errors.addressLine1}
          fieldName="addressLine1"
          type="text"
          labelText={tInvoiceDetails('label.address-line-1')}
          helperText={tInvoiceDetails('helper-text.address-line-1')}
          maxLength={255}
          isRequired
        />

        <FormField
          {...sharedProps}
          error={errors.addressLine2}
          fieldName="addressLine2"
          type="text"
          labelText={tInvoiceDetails('label.address-line-2')}
          helperText={tInvoiceDetails('helper-text.address-line-2')}
          maxLength={255}
        />

        <Stack preset="default">
          <FormField
            {...sharedProps}
            error={errors.country}
            fieldName="country"
            labelText={tInvoiceDetails('label.country')}
            isRequired
          >
            <CountryDropdown
              ref={register({ required: tInvoiceDetails<string>('error.required') })}
              isInvalid={!!errors.country}
              placeholder={tInvoiceDetails('placeholder.country')}
            />
          </FormField>

          <FormField
            {...sharedProps}
            error={errors.city}
            fieldName="city"
            type="text"
            labelText={tInvoiceDetails('label.city')}
            maxLength={40}
            isRequired
          />

          <FormField
            {...sharedProps}
            error={errors.zipCode}
            fieldName="zipCode"
            type="text"
            labelText={tInvoiceDetails('label.zip-code')}
            maxLength={20}
            isRequired
          />
        </Stack>

        <Stack preset="default">
          {shouldShowState && (
            <>
              <FormField
                {...sharedProps}
                error={errors.state}
                fieldName="state"
                labelText={tInvoiceDetails('label.state')}
                isRequired
              >
                <StateDropdown
                  ref={register({ required: tInvoiceDetails<string>('error.required') })}
                  isInvalid={!!errors.state}
                  watchCountry={watchCountry}
                  placeholder={tInvoiceDetails('placeholder.state')}
                />
              </FormField>
              <FormField
                {...sharedProps}
                error={errors.county}
                fieldName="county"
                labelText={tInvoiceDetails('label.county')}
                type="text"
                maxLength={32}
              />
            </>
          )}
        </Stack>

        <S.Text as="p" textType="subtitle1">
          {tInvoiceDetails('subtitle.organization-information')}
        </S.Text>

        <FormField
          {...sharedProps}
          error={errors.companyName}
          fieldName="companyName"
          type="text"
          labelText={tInvoiceDetails('label.company-name')}
          placeholder={tInvoiceDetails('placeholder.company-name')}
          isRequired
        />

        <FormField
          {...sharedProps}
          error={errors.taxNumber}
          fieldName="taxNumber"
          type="text"
          labelText={tInvoiceDetails('label.tax-number')}
          helperText={tInvoiceDetails('helper-text.tax-number')}
        />

        {taxMessageString && (
          <S.NoTaxNumberWarningContainer>
            <S.InfoIcon icon="info" />
            {tInvoiceDetails(taxMessageString)}
          </S.NoTaxNumberWarningContainer>
        )}
      </Stack>
    </form>
  );
}
